/**
 * The effects layer (TASKS T58, SPEC §2 `fx/canvas.ts`, BRIEF <stack>).
 *
 * > HTML5 Canvas for the effects layer only — **never for text the user reads or edits**.
 *
 * That rule is the whole design constraint, and it is honoured structurally rather than by care:
 * nothing in this module calls `fillText` or `strokeText`, and `tests/e2e/fx.spec.ts` replaces both
 * with a throwing stub before the app boots and then plays a whole ronda. Text on a canvas does not
 * scale with the OS text size, cannot be selected, found, or read by a screen reader, and at minimum
 * brightness in a dark room it is the difference between a usable app and a blurry one.
 *
 * ## What it draws, and what it refuses to
 *
 * A slow drift of dim points behind the card stack. That is all. In particular:
 *
 *  - **It never judges.** The pulse on a banked answer is the same colour and the same shape whether
 *    the answer was right or wrong. A red flash on a failure is a punishment signal, and nothing in
 *    this game turns a lapse into a loss.
 *  - **It never carries information.** Nothing the learner needs is here, so it can be switched off,
 *    fail to paint, or be covered entirely and the game is unchanged.
 *  - **It stays out of the way of contrast.** Everything is drawn at low alpha over the page
 *    background, because the readable text sits on top and usage scenario 1 is a phone at minimum
 *    brightness.
 *
 * ## Reduced motion
 *
 * With `prefers-reduced-motion: reduce`, the field is painted once and never moves, and a bank pulses
 * nothing. Not "less motion" — none. The setting is a request from someone for whom movement is a
 * symptom, not a preference about taste.
 *
 * The simulation is pure and separated from the drawing, so the interesting part is unit-testable
 * without a canvas.
 */

import type { Rng } from '../core/rng';

/** One drifting point. Positions are fractions of the viewport, so a resize needs no rescaling. */
export interface Mote {
  /** 0..1 across. */
  x: number;
  /** 0..1 down. */
  y: number;
  /** Fractions of the viewport per second. */
  vx: number;
  vy: number;
  /** Device-independent pixels. */
  radius: number;
  alpha: number;
}

export interface FieldOptions {
  count?: number;
  /** Slowest and fastest drift, in fractions of the viewport per second. */
  speed?: readonly [number, number];
  radius?: readonly [number, number];
  alpha?: readonly [number, number];
}

/**
 * Texture, not a scene.
 *
 * Made denser and larger after a play session ("boring, makes it feel like a task") — and
 * deliberately **not** brighter. Body text is drawn over this field in the display region, so a
 * mote's peak alpha is bounded by the contrast floor rather than by taste: at 0.16 over the ground,
 * the dimmest ink still clears 7:1 against the brightest tier hue, and at 0.22 it does not.
 * `tests/unit/fx.spec.ts` computes that bound from the palette rather than restating the number, so
 * a future hue that is brighter than today's cannot quietly slip under it.
 *
 * More of them and bigger costs nothing: the peak luminance of a 3px dot is the same as a 1px dot's.
 * That is where the richness came from.
 */
export const DEFAULT_FIELD: Required<FieldOptions> = {
  count: 90,
  // A mote crosses the screen in something between three and fifteen minutes. Motion that is
  // *noticeable* is motion that competes with the card.
  speed: [0.0012, 0.0055],
  radius: [0.7, 2.8],
  alpha: [0.05, 0.155],
};

function between(rng: Rng, range: readonly [number, number]): number {
  return range[0] + rng.next() * (range[1] - range[0]);
}

/** Build the field from the injected stream, so the same seed paints the same background. */
export function makeField(rng: Rng, options: FieldOptions = {}): Mote[] {
  const spec = { ...DEFAULT_FIELD, ...options };
  return Array.from({ length: spec.count }, () => {
    const angle = rng.next() * Math.PI * 2;
    const speed = between(rng, spec.speed);
    return {
      x: rng.next(),
      y: rng.next(),
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      radius: between(rng, spec.radius),
      alpha: between(rng, spec.alpha),
    };
  });
}

/**
 * Advance the field by `dtMs`. Pure: returns a new array and mutates nothing.
 *
 * Motes wrap rather than respawn. A respawning point pops into existence somewhere, and the eye
 * catches a pop from across a dark room far more readily than it catches slow drift.
 */
export function stepField(motes: readonly Mote[], dtMs: number): Mote[] {
  const dt = dtMs / 1000;
  return motes.map((mote) => ({
    ...mote,
    x: wrap(mote.x + mote.vx * dt),
    y: wrap(mote.y + mote.vy * dt),
  }));
}

function wrap(value: number): number {
  if (value >= 0 && value < 1) return value;
  const wrapped = value % 1;
  return wrapped < 0 ? wrapped + 1 : wrapped;
}

// ---------------------------------------------------------------------------------------------
// Pulses
// ---------------------------------------------------------------------------------------------

/** A ring expanding from where something was banked. One colour, whatever the answer was. */
export interface Pulse {
  x: number;
  y: number;
  /** 0 at birth, 1 when spent. */
  age: number;
  /** Milliseconds from 0 to 1. */
  lifeMs: number;
}

export const PULSE_LIFE_MS = 900;

export function stepPulses(pulses: readonly Pulse[], dtMs: number): Pulse[] {
  return pulses
    .map((pulse) => ({ ...pulse, age: pulse.age + dtMs / pulse.lifeMs }))
    .filter((pulse) => pulse.age < 1);
}

/** Radius in fractions of the viewport width, and the alpha to draw it at. */
export function pulseShape(age: number): { radius: number; alpha: number } {
  const eased = 1 - (1 - age) * (1 - age);
  return { radius: 0.04 + eased * 0.5, alpha: 0.18 * (1 - age) };
}

// ---------------------------------------------------------------------------------------------
// The layer
// ---------------------------------------------------------------------------------------------

/** The slice of the 2D context this layer uses. Narrowed so a test can pass a recording double. */
export interface Ctx2D {
  canvas: { width: number; height: number };
  clearRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  arc(x: number, y: number, r: number, from: number, to: number): void;
  fill(): void;
  stroke(): void;
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  globalAlpha: number;
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
}

export interface FxOptions {
  rng: Rng;
  /** No motion at all when true (`prefers-reduced-motion: reduce`). */
  reducedMotion?: boolean;
  /** Device pixel ratio. Injected so a test can pin it. */
  pixelRatio?: number;
  field?: FieldOptions;
  /** The ink colour. Defaults to the palette's dim ink; the app hands it the current card's hue. */
  colour?: string;
}

/**
 * The canvas effects layer.
 *
 * Holds no game state and is told nothing about correctness — `bank()` takes a position and nothing
 * else, on purpose. A layer that knew whether the answer was right could eventually be asked to say
 * so, and this one must never be able to.
 */
export class FxLayer {
  private motes: Mote[];
  private pulses: Pulse[] = [];
  private readonly reducedMotion: boolean;
  private readonly pixelRatio: number;
  private colour: string;
  private width = 0;
  private height = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement | null,
    options: FxOptions,
  ) {
    this.motes = makeField(options.rng.fork('fx'), options.field ?? {});
    this.reducedMotion = options.reducedMotion ?? false;
    this.pixelRatio = options.pixelRatio ?? 1;
    this.colour = options.colour ?? '#a4adba';
  }

  /**
   * Repaint in a new colour (T59d).
   *
   * The field takes the hue of the card on screen, so the whole page belongs to the tier being
   * practised rather than to nothing in particular. Colour only — nothing about the simulation
   * changes, and the layer still knows nothing about what is being asked or how it went.
   */
  setColour(colour: string): void {
    this.colour = colour;
  }

  /** Match the backing store to the canvas's CSS size. Cheap, and safe to call every frame. */
  resize(cssWidth: number, cssHeight: number): void {
    if (!this.canvas) return;
    if (cssWidth === this.width && cssHeight === this.height) return;
    this.width = cssWidth;
    this.height = cssHeight;
    this.canvas.width = Math.max(1, Math.round(cssWidth * this.pixelRatio));
    this.canvas.height = Math.max(1, Math.round(cssHeight * this.pixelRatio));
  }

  /**
   * Advance the simulation. Called from the fixed update, never from render.
   *
   * With reduced motion the field is frozen and pulses are dropped rather than run at zero speed —
   * a pulse that exists but is invisible is still a thing the next change could accidentally reveal.
   */
  update(dtMs: number): void {
    if (this.reducedMotion) {
      this.pulses = [];
      return;
    }
    this.motes = stepField(this.motes, dtMs);
    this.pulses = stepPulses(this.pulses, dtMs);
  }

  /**
   * An item was banked. Position is a fraction of the viewport.
   *
   * Takes no verdict, and never will. The same ripple for a right answer and a wrong one is not an
   * oversight — a red flash on a failure is a punishment signal, and this game does not have those.
   */
  bank(x = 0.5, y = 0.72): void {
    if (this.reducedMotion) return;
    this.pulses = [...this.pulses, { x, y, age: 0, lifeMs: PULSE_LIFE_MS }];
  }

  /** Paint. Nothing here draws text, and nothing here ever may. */
  render(ctx: Ctx2D | null = this.context()): void {
    if (!ctx) return;
    const { width, height } = ctx.canvas;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const scale = this.pixelRatio;
    ctx.fillStyle = this.colour;
    for (const mote of this.motes) {
      /*
       * Fainter toward the bottom of the screen.
       *
       * The control deck is down there and the motes pass behind it, so a mote that stayed bright
       * all the way down would appear to be cut in half by the deck's top edge. Fading them out
       * across the lower third means they simply are not there by the time they reach it.
       */
      ctx.globalAlpha = mote.alpha * fadeAt(mote.y);
      ctx.beginPath();
      ctx.arc(mote.x * width, mote.y * height, mote.radius * scale, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.strokeStyle = this.colour;
    for (const pulse of this.pulses) {
      const shape = pulseShape(pulse.age);
      ctx.globalAlpha = shape.alpha;
      ctx.lineWidth = scale;
      ctx.beginPath();
      ctx.arc(pulse.x * width, pulse.y * height, shape.radius * width, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.globalAlpha = 1;
  }

  /** For tests: the current field and pulses, copied. */
  snapshot(): { motes: Mote[]; pulses: Pulse[] } {
    return { motes: this.motes.map((m) => ({ ...m })), pulses: this.pulses.map((p) => ({ ...p })) };
  }

  private context(): Ctx2D | null {
    return (this.canvas?.getContext('2d') as unknown as Ctx2D | null) ?? null;
  }
}

/**
 * How much of a mote's alpha survives at this height, 0..1.
 *
 * Full above the thumb line, gone by the bottom. See the note in `render`.
 */
export function fadeAt(y: number): number {
  const start = 0.45;
  if (y <= start) return 1;
  return Math.max(0, 1 - (y - start) / (1 - start));
}
